import { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import { MetricsState, useMetricsPublisher, MetricType } from "../logging";
import Api from "./Api";
import http from "./http";

const isAbsolutePathRegex = /^[a-z][a-z\d+.-]*:/;

const api = new Api(process.env.API_BASE_URL);

/**
 * Sends an http request with metrics.
 * TODO: all http requests should be refactored to use this function.
 *
 * @template T
 * @param {string} actionName
 * @param {MetricsState} metrics
 * @param {AxiosRequestConfig} requestConfig
 * @returns {Promise<AxiosResponse<T>>}
 */
async function httpRequestWithMetrics<T>(
  actionName: string,
  metrics: MetricsState,
  requestConfig: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
  if (!requestConfig.url) {
    throw new Error("Must include url for request.");
  }

  const metricsPublisher = useMetricsPublisher(metrics, actionName);

  // Url prefixed with api endpoint url if request is relative.
  const url = !isAbsolutePathRegex.test(requestConfig.url)
    ? api.getEndpointUri(requestConfig.url)
    : requestConfig.url;

  const transformedConfig = {
    ...requestConfig,
    url,
  };

  const httpMetric = new MetricType.HttpRequest(actionName).withMonitor();
  httpMetric.url = transformedConfig.url;

  try {
    const response = await http.request<T>(transformedConfig);

    httpMetric.statusCode = response.status;
    httpMetric.statusText = response.statusText;
    httpMetric.setSuccess();

    return response;
  } catch (error) {
    httpMetric.setFailure();

    const httpError = error as AxiosError;
    if (httpError.response) {
      httpMetric.statusCode = httpError.response.status;
      httpMetric.statusText = httpError.response.statusText;
    } else {
      console.error(error);
    }

    throw error;
  } finally {
    metricsPublisher.publish(httpMetric);
  }
}

export default httpRequestWithMetrics;
